import { supabase } from '@/lib/supabase';

/**
 * Google sign-in — ported verbatim from the OG WINR (`handleGoogleLogin` +
 * `exchangeGoogleTokenAndUpsert`, js/app.js:569). Uses Google Identity Services
 * (the same client ID + scope) and upserts into the legacy `users` table.
 *
 * ⚠️ Like the OG, this bypasses Supabase Auth — it does NOT create a Supabase
 * session, so `AuthProvider` / `RequireAuth` won't treat it as signed in. To make
 * Google a first-class V2 login, enable the Google provider in Supabase and call
 * `supabase.auth.signInWithOAuth({ provider: 'google' })` instead.
 */

const GOOGLE_CLIENT_ID =
  '716963575827-mre7ghoukk30l22q2tk8o27d96au3m9e.apps.googleusercontent.com';
const GSI_SRC = 'https://accounts.google.com/gsi/client';

interface TokenResponse {
  access_token?: string;
}
interface TokenClient {
  requestAccessToken: () => void;
}
interface GoogleAccounts {
  oauth2: {
    initTokenClient: (config: {
      client_id: string;
      scope: string;
      callback: (resp: TokenResponse) => void | Promise<void>;
    }) => TokenClient;
  };
}
declare global {
  interface Window {
    google?: { accounts: GoogleAccounts };
  }
}

let tokenClient: TokenClient | null = null;

/** Inject the GSI script once (the OG loads it via a <script> in index.html). */
function loadGsi(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('GSI failed to load')));
      return;
    }
    const s = document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('GSI failed to load'));
    document.head.appendChild(s);
  });
}

/**
 * Exchange the Google access token for the profile, then upsert into `users`.
 * Mirrors OG `exchangeGoogleTokenAndUpsert` (js/supabase.js:258).
 */
async function exchangeGoogleTokenAndUpsert(accessToken: string) {
  const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
    headers: { Authorization: 'Bearer ' + accessToken },
  });
  if (!res.ok) throw new Error('Google userinfo request failed: ' + res.status);
  const profile = (await res.json()) as {
    sub: string;
    email?: string;
    name?: string;
    picture?: string;
  };
  const { data, error } = await supabase
    .from('users')
    .upsert(
      {
        id: profile.sub,
        email: profile.email,
        display_name: profile.name,
        avatar_url: profile.picture,
        auth_provider: 'google',
      },
      { onConflict: 'id' },
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Trigger the Google sign-in popup. Resolves once the upsert completes. */
export async function signInWithGoogle(): Promise<void> {
  await loadGsi();
  if (!window.google?.accounts) {
    throw new Error('Google Identity Services not available');
  }
  await new Promise<void>((resolve, reject) => {
    tokenClient ??= window.google!.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'email profile openid',
      callback: async (resp: TokenResponse) => {
        if (!resp?.access_token) return reject(new Error('No access token'));
        try {
          await exchangeGoogleTokenAndUpsert(resp.access_token);
          resolve();
        } catch (err) {
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      },
    });
    tokenClient.requestAccessToken();
  });
}
